import { useCallback, useEffect, useMemo } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";

import { useBoards } from "../../../../../reactQuery/boards";
import { useWorkflow } from "../../../../../reactQuery/workflows";
import { MergeBoards } from "../../../../../services/apiBoards";
import useToastPromise from "../../../../../hooks/useToast";
import { getResponseData } from "../../../../../reactQuery/response";
import {
  buildWorkflowStepExchangeId,
  getActiveSourceBoard,
  getSourceBoardOptions,
  getTargetBoardOptions,
  getWorkflowStepsByWorkflowId,
} from "./mergeBoardUtils";

export function useMergeBoardState({ admin, close, currentBoard }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { showToastPromise } = useToastPromise();
  const { boards } = useBoards();
  const { workflows } = useWorkflow();
  const sourceBoardId = currentBoard?.board_id ?? null;
  const workflowOptions = useMemo(() => getResponseData(workflows, []), [workflows]);

  const sourceBoardOptions = useMemo(
    () => getSourceBoardOptions(getResponseData(boards, [])),
    [boards]
  );

  const methods = useForm({
    defaultValues: {
      sourceBoard: sourceBoardId || "",
      targetStep: "",
      workflow_step_exchange_id: {},
    },
  });
  const { handleSubmit, reset, watch } = methods;

  const selectedSourceBoardId = watch("sourceBoard");
  const selectedTargetBoardId = watch("targetStep");
  const normalizedSelectedSourceBoardId = selectedSourceBoardId
    ? Number(selectedSourceBoardId)
    : null;
  const normalizedSelectedTargetBoardId = selectedTargetBoardId
    ? Number(selectedTargetBoardId)
    : null;
  const activeSourceBoardId = normalizedSelectedSourceBoardId ?? sourceBoardId;
  const activeSourceBoard = useMemo(
    () =>
      getActiveSourceBoard({
        sourceBoardOptions,
        activeSourceBoardId,
        currentBoard,
      }),
    [activeSourceBoardId, currentBoard, sourceBoardOptions]
  );
  const targetBoardOptions = useMemo(
    () => getTargetBoardOptions(sourceBoardOptions, activeSourceBoardId),
    [activeSourceBoardId, sourceBoardOptions]
  );
  const sourceWorkflowId = activeSourceBoard?.workflow_id ?? null;

  const workflowOfSource = useMemo(
    () =>
      workflowOptions.find((item) => item?.workflow_id === sourceWorkflowId) ??
      null,
    [sourceWorkflowId, workflowOptions]
  );
  const targetWorkflowId = useMemo(
    () =>
      targetBoardOptions.find(
        (board) => board.board_id === normalizedSelectedTargetBoardId
      )
        ?.workflow_id ?? null,
    [normalizedSelectedTargetBoardId, targetBoardOptions]
  );
  const sourceWorkflowSteps = useMemo(
    () => getWorkflowStepsByWorkflowId(workflowOptions, sourceWorkflowId),
    [sourceWorkflowId, workflowOptions]
  );
  const targetWorkflowSteps = useMemo(
    () => getWorkflowStepsByWorkflowId(workflowOptions, targetWorkflowId),
    [targetWorkflowId, workflowOptions]
  );
  const sourceWorkflowName = workflowOfSource?.workflow_name || "Source board";
  const canSelectTargetBoard = !admin || Boolean(selectedSourceBoardId);
  const shouldShowWorkflowMapping =
    Boolean(targetWorkflowId) && sourceWorkflowId !== targetWorkflowId;

  useEffect(() => {
    reset({
      sourceBoard: activeSourceBoardId || "",
      targetStep: normalizedSelectedTargetBoardId || "",
      workflow_step_exchange_id: buildWorkflowStepExchangeId(
        sourceWorkflowSteps,
        targetWorkflowSteps
      ),
    });
  }, [
    activeSourceBoardId,
    normalizedSelectedTargetBoardId,
    reset,
    sourceWorkflowSteps,
    targetWorkflowSteps,
  ]);

  const { mutate: mergeBoardMutate, isPending } = useMutation({
    mutationFn: async (data) => showToastPromise(MergeBoards, data),
    onSuccess: (_response, variables) => {
      const targetBoard = targetBoardOptions.find(
        (board) => board.board_id === variables.target_board_id
      );

      queryClient.invalidateQueries({
        queryKey: ["boards"],
      });
      queryClient.invalidateQueries({
        queryKey: ["cards",variables.target_board_id],
      });
      queryClient.invalidateQueries({
        queryKey: ["boardName", variables.target_board_id],
      });
      navigate(`/board/${targetBoard?.key || variables.target_board_id}`);
      close(false);
      reset({
        sourceBoard: sourceBoardId || "",
        targetStep: "",
        workflow_step_exchange_id: {},
      });
    },
  });

  const handleMergeBoard = useMemo(
    () =>
      handleSubmit((data) => {
        const nextTargetBoardId = Number(data?.targetStep);

        mergeBoardMutate({
          source_board_id: activeSourceBoardId,
          target_board_id: nextTargetBoardId,
          workflow_step_exchange_id: JSON.stringify(
            data?.workflow_step_exchange_id || {}
          ),
        });
      }),
    [activeSourceBoardId, handleSubmit, mergeBoardMutate]
  );

  const handleClose = useCallback(() => {
    reset({
      sourceBoard: sourceBoardId || "",
      targetStep: "",
      workflow_step_exchange_id: {},
    });
    close(false);
  }, [close, reset, sourceBoardId]);

  return {
    canSelectTargetBoard,
    handleClose,
    handleMergeBoard,
    isPending,
    methods,
    selectedTargetBoardId,
    shouldShowWorkflowMapping,
    sourceBoardOptions,
    sourceWorkflowName,
    sourceWorkflowSteps,
    targetWorkflowSteps,
    targetBoardOptions,
  };
}
